<template>
  <div :class="$style.container">
    <div :class="$style.topButtonRow">
      <div :class="$style.backButton" @click="handleBackClick">
        <NsIcon icon-name="icon-chevron-left" />
        <div :class="$style.backLink">{{ backText }}</div>
      </div>
      <div
        data-testid="print-button"
        :class="$style.printButton"
        title="print"
        @click="handlePrint"
      >
        <NsIcon icon-name="icon-print" />
      </div>
    </div>
    <div :class="$style.success">
      {{ successMessage }}
    </div>
    <div :class="$style.receipt">
      <div :class="$style.headerImgWrapper">
        <div :class="$style.headerIconWrapper">
          <div :class="$style.headerIcon" />
        </div>
      </div>
      <div :class="$style.title">{{ receiptFromText }} {{ description }}</div>
      <div :class="$style.receiptId">{{ receiptLabel }} #{{ receiptId }}</div>
      <div :class="$style.content">
        <div :class="$style.receiptDetails">
          <div>
            <div :class="$style.subtitle">{{ amountPaidLabel }}</div>
            <div>${{ amount }}</div>
          </div>
          <div>
            <div :class="$style.subtitle">{{ datePaidLabel }}</div>
            <div>{{ transactionDate }}</div>
          </div>
          <div>
            <div :class="$style.subtitle">{{ paymentMethodLabel }}</div>
            <div>**** {{ cardLast4 }}</div>
          </div>
        </div>
        <div :class="$style.subtitle">{{ summaryLabel }}</div>
        <div :class="$style.lineItems">
          <div :class="$style.lineItem">
            <div>{{ nuskinDonationLabel }}</div>
            <div>${{ amount }}</div>
          </div>
          <div :class="$style.lineItem">
            <div>{{ amountChargedLabel }}</div>
            <div>${{ amount }}</div>
          </div>
        </div>
        <div :class="$style.separator" />
        <div :class="$style.questions">
          {{ questionsMessage }}
        </div>
        <div :class="$style.separator" />
        <div :class="$style.info">
          <p>{{ sentToEmailMessage }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { NsIcon } from "@nuskin/ns-icon";
export default {
  name: "NsDonationReceipt",
  components: { NsIcon },
  props: {
    /**
     * Transaction description
     */
    description: {
      type: String,
      default: ""
    },
    /**
     * Transaction receiptId
     */
    receiptId: {
      type: String,
      default: ""
    },
    /**
     * Transaction amount
     */
    amount: {
      type: String,
      default: ""
    },
    /**
     * Transaction transaction date
     */
    transactionDate: {
      type: String,
      default: ""
    },
    /**
     * Transaction cardLast4
     */
    cardLast4: {
      type: String,
      default: ""
    },
    /**
     * Back button Text
     */
    backText: {
      type: String,
      default: "Back to donation form"
    },
    /**
     * Success Message above receipt
     */
    successMessage: {
      type: String,
      default: "Your payment was successful!"
    },
    /**
     * 'Receipt From' translation
     */
    receiptFromText: {
      type: String,
      default: "Receipt from"
    },
    /**
     * 'Receipt' translation'
     */
    receiptLabel: {
      type: String,
      default: "Receipt"
    },
    /**
     * 'Amount Paid' translation
     */
    amountPaidLabel: {
      type: String,
      default: "Amount Paid"
    },
    /**
     * 'Date Paid' translation
     */
    datePaidLabel: {
      type: String,
      default: "Date Paid"
    },
    /**
     * 'Payment Method' translation
     */
    paymentMethodLabel: {
      type: String,
      default: "Payment Method"
    },
    /**
     * 'Summary' translation
     */
    summaryLabel: {
      type: String,
      default: "Summary"
    },
    /**
     * 'Nu Skin Donation' translation
     */
    nuskinDonationLabel: {
      type: String,
      default: "Nu Skin Donation"
    },
    /**
     * 'Amount charged' translation
     */
    amountChargedLabel: {
      type: String,
      default: "Amount charged"
    },
    /**
     * Questions at bottom of receipt
     */
    questionsMessage: {
      type: String,
      default: "If you have any questions, call us at +1 801-345-0157."
    },
    /**
     * Email message at bottom of receipt
     */
    sentToEmailMessage: {
      type: String,
      default: "A copy of this receipt has been sent to your email."
    }
  },
  methods: {
    handleBackClick() {
      this.$emit("back");
    },
    handlePrint() {
      window.print();
    }
  }
};
</script>

<style lang="scss" module>
.container {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1000px;
  width: 100%;
  color: rgba(0, 0, 0, 0.87);
}

.topButtonRow {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 15px 0;
  width: 100%;
}

.backButton {
  display: flex;
  align-items: center;
  padding: 5px;
  cursor: pointer;

  &:hover .backLink {
    border-bottom: 1px solid currentColor;
  }
}

.printButton {
  display: flex;
  align-items: center;
  padding: 5px;
  cursor: pointer;
}

.backLink {
  border-top: 1px solid transparent;
  border-bottom: 1px solid transparent;
}

.success {
  margin: 40px 0;
  padding: 15px;
  width: 100%;
  max-width: 600px;
  background: rgb(211, 250, 219);
  font-size: 24px;
  text-align: center;
}

.receipt {
  padding-bottom: 60px;
  max-width: 600px;
  color: rgba(0, 0, 0, 0.87);
  background: #ffffff;
}

.headerImgWrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 600px;
  min-width: 300px;
  height: 120px;
  background: linear-gradient(
    169deg,
    rgba(0, 77, 117, 1) 50%,
    rgba(255, 255, 255, 1) 50%
  );
}

.headerIconWrapper {
  width: 60px;
  height: 60px;
  background: #ffffff;
  border-radius: 30px;
  box-shadow: 0px 2px 8px 2px rgba(0, 0, 0, 0.14);
}

.headerIcon {
  width: 60px;
  height: 60px;
  background-size: 60px 60px;
  background-position: center;
  background-repeat: no-repeat;
  background-image: url("data:image/png;base64,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");
}

.headerImg {
  object-fit: contain;
}

.title {
  margin: 16px 0;
  padding: 0 20px;
  color: rgba(0, 0, 0, 0.87);
  font-size: 24px;
  font-weight: 400;
  text-align: center;
}

.receiptId {
  margin-bottom: 20px;
  color: rgba(0, 0, 40, 0.54);
  font-size: 16px;
  text-align: center;
}

.content {
  padding: 10px 60px;
}

.receiptDetails {
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
}

.subtitle {
  margin-bottom: 5px;
  color: rgba(0, 0, 40, 0.54);
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
}

.lineItems {
  margin: 20px 0;
  padding: 0 20px;
  width: 100%;
  background: rgb(244, 246, 248);
}

.lineItem {
  display: flex;
  justify-content: space-between;
  padding: 20px 0;
  border-bottom: 1px solid rgba(0, 0, 40, 0.14);
  width: 100%;

  &:last-child {
    border-bottom: none;
    font-weight: 600;
  }
}

.separator {
  margin: 40px 0;
  width: 100%;
  height: 1px;
  background: rgb(206, 208, 208);
}

.questions {
  font-size: 16px;
  line-height: 24px;
}

.info p {
  color: rgba(0, 0, 40, 0.54);
  font-size: 13px;
}
</style>
